import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArtDirectedImagePreload, ShopifyImage } from '@/components/ui/shopify-image';
import { type CmsMediaLike, getCmsMediaAlt, getCmsMediaUrl } from '@/lib/cms-media';
import { getDescriptiveLinkText } from './descriptive-link-text';
import { HeroDropCountdown } from './hero-drop-countdown';
import { getSectionId } from './section-id';
import { getSectionSpacing } from './section-spacing';
interface CtaButton {
  label?: string;
  url?: string;
  style?: string;
}
interface HeroBannerProps {
  block: Record<string, unknown>;
}
const MOBILE_HERO_IMAGE_SIZES = '(max-width: 767px) 50vw, 100vw';
function renderHeadingWithBreak(heading: string, breakAfter?: string) {
  const phrase = breakAfter?.trim();
  if (!phrase) return heading;
  const index = heading.indexOf(phrase);
  if (index === -1) return heading;
  const splitIndex = index + phrase.length;
  const before = heading.slice(0, splitIndex);
  const after = heading.slice(splitIndex).trimStart();
  return <>
			{before}
			{after && <>
					<br />
					{after}
				</>}
		</>;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: CMS section renderer needs several visual branches
export function HeroBannerBlock(t0) {
  const $ = _c(122);
  const {
    block
  } = t0;
  const sectionSpacing = block.sectionSpacing as string | undefined;
  const isFirstBlock = block._isFirstBlock === true;
  const isNested = block._nested === true;
  let t1;
  if ($[0] !== block || $[1] !== isNested) {
    t1 = isNested ? undefined : getSectionId(block);
    $[0] = block;
    $[1] = isNested;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sectionId = t1;
  const heading = block.heading as string;
  const headingBreakAfter = block.headingBreakAfter as string | undefined;
  const subheading = block.subheading as string | undefined;
  const bgImage = block.backgroundImage as CmsMediaLike | undefined;
  const mobileBgImage = block.mobileBackgroundImage as CmsMediaLike | undefined;
  let t2;
  if ($[3] !== bgImage) {
    t2 = getCmsMediaUrl(bgImage, ["hero"]);
    $[3] = bgImage;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const bgImageUrl = t2;
  let t3;
  if ($[5] !== mobileBgImage) {
    t3 = getCmsMediaUrl(mobileBgImage, ["mobileHero", "card", "hero"]);
    $[5] = mobileBgImage;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const mobileBgImageUrl = t3;
  const overlay = block.overlay as string || "medium";
  const textColor = block.textColor as string || "white";
  const height = block.height as string || "large";
  const eyebrowSize = block.eyebrowSize as string || "medium";
  const headingSize = block.headingSize as string || "large";
  const subheadingSize = block.subheadingSize as string || "medium";
  const ctaSize = block.ctaSize as string || "medium";
  const primaryCta = block.primaryCta as CtaButton | undefined;
  const secondaryCta = block.secondaryCta as CtaButton | undefined;
  const eyebrow = block.eyebrow as string | undefined;
  const textAlignment = block.textAlignment as string || "center";
  const showDropCountdown = block.showDropCountdown === true;
  let t4;
  if ($[7] !== block.dropAt) {
    const dropAtRaw = typeof block.dropAt === "string" ? Date.parse(block.dropAt) : Number.NaN;
    t4 = Number.isFinite(dropAtRaw) ? dropAtRaw : undefined;
    $[7] = block.dropAt;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const dropTargetMs = t4;
  let t5;
  if ($[9] !== primaryCta?.label) {
    t5 = primaryCta?.label?.trim() ?? "";
    $[9] = primaryCta?.label;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const primaryCtaLabel = t5;
  let t6;
  if ($[11] !== secondaryCta?.label) {
    t6 = secondaryCta?.label?.trim() ?? "";
    $[11] = secondaryCta?.label;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const secondaryCtaLabel = t6;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t7;
  let t8;
  let t9;
  if ($[13] !== bgImage || $[14] !== bgImageUrl || $[15] !== ctaSize || $[16] !== dropTargetMs || $[17] !== eyebrow || $[18] !== eyebrowSize || $[19] !== heading || $[20] !== headingBreakAfter || $[21] !== headingSize || $[22] !== height || $[23] !== isFirstBlock || $[24] !== isNested || $[25] !== mobileBgImage || $[26] !== mobileBgImageUrl || $[27] !== overlay || $[28] !== primaryCta || $[29] !== primaryCtaLabel || $[30] !== secondaryCta || $[31] !== secondaryCtaLabel || $[32] !== sectionId || $[33] !== sectionSpacing || $[34] !== showDropCountdown || $[35] !== subheading || $[36] !== subheadingSize || $[37] !== textAlignment || $[38] !== textColor) {
    const primaryCtaAriaLabel = getDescriptiveLinkText({
      label: primaryCta?.label,
      context: heading,
      url: primaryCta?.url
    });
    const secondaryCtaAriaLabel = getDescriptiveLinkText({
      label: secondaryCta?.label,
      context: heading,
      url: secondaryCta?.url
    });
    const HeadingTag = isFirstBlock && !isNested ? "h1" : "h2";
    let t21;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = {
        full: "min-h-screen",
        large: "h-[85vh]",
        medium: "h-[60vh]",
        small: "h-[40vh]"
      };
      $[53] = t21;
    } else {
      t21 = $[53];
    }
    const heightClasses = t21;
    let t22;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = {
        full: {
          minHeight: "100vh"
        },
        large: {
          height: "85vh"
        },
        medium: {
          height: "60vh"
        },
        small: {
          height: "40vh"
        }
      };
      $[54] = t22;
    } else {
      t22 = $[54];
    }
    const heightStyle = t22;
    let t23;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = {
        full: "pb-12 pt-40 sm:pb-16 lg:pb-20",
        large: "pb-10 pt-32 sm:pb-12 lg:pb-16",
        medium: "pb-8 pt-24 sm:pb-10 lg:pb-12",
        small: "pb-6 pt-20 sm:pb-8 lg:pb-10"
      };
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    const contentPaddingClasses = t23;
    let t24;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = {
        none: "",
        light: "bg-gradient-to-t from-black/35 via-black/12 to-black/8",
        medium: "bg-gradient-to-t from-black/55 via-black/24 to-black/14",
        dark: "bg-gradient-to-t from-black/75 via-black/40 to-black/24"
      };
      $[56] = t24;
    } else {
      t24 = $[56];
    }
    const overlayClasses = t24;
    const isLight = textColor !== "white";
    let t25;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = {
        left: "text-left items-start",
        center: "text-center items-center",
        right: "text-right items-end"
      };
      $[57] = t25;
    } else {
      t25 = $[57];
    }
    const alignmentClasses = t25;
    let t26;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = {
        left: "",
        center: "mx-auto",
        right: "ml-auto"
      };
      $[58] = t26;
    } else {
      t26 = $[58];
    }
    const subtitleAlignmentClasses = t26;
    let t27;
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = {
        left: "justify-start",
        center: "justify-center",
        right: "justify-end"
      };
      $[59] = t27;
    } else {
      t27 = $[59];
    }
    const ctaAlignmentClasses = t27;
    let t28;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = {
        small: "text-[11px] tracking-[0.24em]",
        medium: "text-xs tracking-[0.3em]",
        large: "text-sm tracking-[0.34em]"
      };
      $[60] = t28;
    } else {
      t28 = $[60];
    }
    const eyebrowSizeClasses = t28;
    let t29;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = {
        small: "text-3xl sm:text-4xl lg:text-5xl xl:text-6xl",
        medium: "text-4xl sm:text-5xl lg:text-6xl xl:text-7xl",
        large: "text-5xl sm:text-6xl lg:text-7xl xl:text-8xl"
      };
      $[61] = t29;
    } else {
      t29 = $[61];
    }
    const headingSizeClasses = t29;
    let t30;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = {
        small: "text-sm sm:text-base",
        medium: "text-base sm:text-lg",
        large: "text-lg sm:text-xl"
      };
      $[62] = t30;
    } else {
      t30 = $[62];
    }
    const subheadingSizeClasses = t30;
    let t31;
    if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = {
        small: "px-6 py-3 text-xs",
        medium: "px-8 py-3.5 text-sm",
        large: "px-10 py-4 text-base"
      };
      $[63] = t31;
    } else {
      t31 = $[63];
    }
    const ctaSizeClasses = t31;
    t14 = sectionId;
    const t32 = heightClasses[height];
    const t33 = isNested ? "none" : sectionSpacing || "none";
    let t34;
    if ($[64] !== t33) {
      t34 = getSectionSpacing(t33);
      $[64] = t33;
      $[65] = t34;
    } else {
      t34 = $[65];
    }
    t15 = `relative flex ${t32} flex-col justify-end overflow-hidden bg-neutral-950 ${t34}`;
    t16 = heightStyle[height] || heightStyle.large;
    if ($[66] !== bgImageUrl || $[67] !== isFirstBlock || $[68] !== mobileBgImageUrl) {
      t17 = isFirstBlock && bgImageUrl && mobileBgImageUrl && <ArtDirectedImagePreload variants={[{
        src: bgImageUrl,
        sizes: "100vw",
        media: "(min-width: 768px)"
      }, {
        src: mobileBgImageUrl,
        sizes: MOBILE_HERO_IMAGE_SIZES,
        media: "(max-width: 767px)"
      }]} />;
      $[66] = bgImageUrl;
      $[67] = isFirstBlock;
      $[68] = mobileBgImageUrl;
      $[69] = t17;
    } else {
      t17 = $[69];
    }
    if ($[70] !== bgImage || $[71] !== bgImageUrl || $[72] !== heading || $[73] !== isFirstBlock || $[74] !== mobileBgImageUrl) {
      t18 = bgImageUrl ? <ShopifyImage src={bgImageUrl} alt={getCmsMediaAlt(bgImage, heading)} fill={true} preload={isFirstBlock && !mobileBgImageUrl} className={mobileBgImageUrl ? "hidden md:block" : ""} style={{
        objectFit: "cover"
      }} loading={isFirstBlock ? "eager" : undefined} fetchPriority={isFirstBlock ? "high" : undefined} decoding={isFirstBlock ? "sync" : undefined} sizes="100vw" /> : null;
      $[70] = bgImage;
      $[71] = bgImageUrl;
      $[72] = heading;
      $[73] = isFirstBlock;
      $[74] = mobileBgImageUrl;
      $[75] = t18;
    } else {
      t18 = $[75];
    }
    if ($[76] !== heading || $[77] !== isFirstBlock || $[78] !== mobileBgImage || $[79] !== mobileBgImageUrl) {
      t19 = mobileBgImageUrl && <ShopifyImage src={mobileBgImageUrl} alt={getCmsMediaAlt(mobileBgImage, heading)} fill={true} className="md:hidden" style={{
        objectFit: "cover"
      }} loading={isFirstBlock ? "eager" : undefined} fetchPriority="high" decoding={isFirstBlock ? "sync" : undefined} sizes={MOBILE_HERO_IMAGE_SIZES} />;
      $[76] = heading;
      $[77] = isFirstBlock;
      $[78] = mobileBgImage;
      $[79] = mobileBgImageUrl;
      $[80] = t19;
    } else {
      t19 = $[80];
    }
    if ($[81] !== overlay) {
      t20 = overlay !== "none" && <div className={`pointer-events-none absolute inset-0 ${overlayClasses[overlay] || overlayClasses.medium}`} />;
      $[81] = overlay;
      $[82] = t20;
    } else {
      t20 = $[82];
    }
    t13 = `container-page relative z-10 ${contentPaddingClasses[height] || contentPaddingClasses.large}`;
    t7 = `flex max-w-3xl flex-col gap-0 ${alignmentClasses[textAlignment] || alignmentClasses.center}`;
    if ($[83] !== eyebrow || $[84] !== eyebrowSize || $[85] !== isLight) {
      t8 = eyebrow && <p className={`mb-4 font-semibold uppercase ${eyebrowSizeClasses[eyebrowSize] || eyebrowSizeClasses.medium} ${isLight ? "text-neutral-500" : "text-white/70"}`}>{eyebrow}</p>;
      $[83] = eyebrow;
      $[84] = eyebrowSize;
      $[85] = isLight;
      $[86] = t8;
    } else {
      t8 = $[86];
    }
    const t35 = `font-black uppercase leading-[0.95] tracking-tight ${headingSizeClasses[headingSize] || headingSizeClasses.large} ${isLight ? "text-neutral-900" : "text-white"}`;
    let t36;
    if ($[87] !== heading || $[88] !== headingBreakAfter) {
      t36 = renderHeadingWithBreak(heading, headingBreakAfter);
      $[87] = heading;
      $[88] = headingBreakAfter;
      $[89] = t36;
    } else {
      t36 = $[89];
    }
    if ($[90] !== HeadingTag || $[91] !== t35 || $[92] !== t36) {
      t9 = <HeadingTag className={t35}>{t36}</HeadingTag>;
      $[90] = HeadingTag;
      $[91] = t35;
      $[92] = t36;
      $[93] = t9;
    } else {
      t9 = $[93];
    }
    if ($[94] !== isLight || $[95] !== subheading || $[96] !== subheadingSize || $[97] !== textAlignment) {
      t10 = subheading && <p className={`mt-4 max-w-xl ${subheadingSizeClasses[subheadingSize] || subheadingSizeClasses.medium} ${isLight ? "text-neutral-600" : "text-white/80"} ${subtitleAlignmentClasses[textAlignment] || ""}`}>{subheading}</p>;
      $[94] = isLight;
      $[95] = subheading;
      $[96] = subheadingSize;
      $[97] = textAlignment;
      $[98] = t10;
    } else {
      t10 = $[98];
    }
    if ($[99] !== dropTargetMs || $[100] !== showDropCountdown || $[101] !== textAlignment) {
      t11 = showDropCountdown && <HeroDropCountdown targetMs={dropTargetMs} alignment={textAlignment} />;
      $[99] = dropTargetMs;
      $[100] = showDropCountdown;
      $[101] = textAlignment;
      $[102] = t11;
    } else {
      t11 = $[102];
    }
    t12 = (primaryCtaLabel || secondaryCtaLabel) && <div className={`mt-8 flex flex-wrap gap-4 ${ctaAlignmentClasses[textAlignment] || ctaAlignmentClasses.center}`}>{primaryCtaLabel && primaryCta?.url && <Link href={primaryCta.url} aria-label={primaryCtaAriaLabel || undefined} className={`inline-flex items-center justify-center rounded bg-white font-bold uppercase tracking-wider text-neutral-900 transition-colors hover:bg-neutral-200 ${ctaSizeClasses[ctaSize] || ctaSizeClasses.medium}`}>{primaryCtaLabel}{primaryCtaAriaLabel && primaryCtaAriaLabel !== primaryCtaLabel && <span className="sr-only">{primaryCtaAriaLabel.replace(/^learn more/i, "")}</span>}</Link>}{secondaryCtaLabel && secondaryCta?.url && <Link href={secondaryCta.url} aria-label={secondaryCtaAriaLabel || undefined} className={`inline-flex items-center justify-center border-b-2 border-white px-1 font-bold uppercase tracking-wider text-white transition-colors hover:border-white/60 hover:text-white/80 ${ctaSize === "small" ? "py-3 text-xs" : ctaSize === "large" ? "py-4 text-base" : "py-3.5 text-sm"}`}>{secondaryCtaLabel}{secondaryCtaAriaLabel && secondaryCtaAriaLabel !== secondaryCtaLabel && <span className="sr-only">{secondaryCtaAriaLabel.replace(/^learn more/i, "")}</span>}</Link>}</div>;
    $[13] = bgImage;
    $[14] = bgImageUrl;
    $[15] = ctaSize;
    $[16] = dropTargetMs;
    $[17] = eyebrow;
    $[18] = eyebrowSize;
    $[19] = heading;
    $[20] = headingBreakAfter;
    $[21] = headingSize;
    $[22] = height;
    $[23] = isFirstBlock;
    $[24] = isNested;
    $[25] = mobileBgImage;
    $[26] = mobileBgImageUrl;
    $[27] = overlay;
    $[28] = primaryCta;
    $[29] = primaryCtaLabel;
    $[30] = secondaryCta;
    $[31] = secondaryCtaLabel;
    $[32] = sectionId;
    $[33] = sectionSpacing;
    $[34] = showDropCountdown;
    $[35] = subheading;
    $[36] = subheadingSize;
    $[37] = textAlignment;
    $[38] = textColor;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
  } else {
    t10 = $[39];
    t11 = $[40];
    t12 = $[41];
    t13 = $[42];
    t14 = $[43];
    t15 = $[44];
    t16 = $[45];
    t17 = $[46];
    t18 = $[47];
    t19 = $[48];
    t20 = $[49];
    t7 = $[50];
    t8 = $[51];
    t9 = $[52];
  }
  let t21;
  if ($[103] !== t10 || $[104] !== t11 || $[105] !== t12 || $[106] !== t7 || $[107] !== t8 || $[108] !== t9) {
    t21 = <div className={t7}>{t8}{t9}{t10}{t11}{t12}</div>;
    $[103] = t10;
    $[104] = t11;
    $[105] = t12;
    $[106] = t7;
    $[107] = t8;
    $[108] = t9;
    $[109] = t21;
  } else {
    t21 = $[109];
  }
  let t22;
  if ($[110] !== t13 || $[111] !== t21) {
    t22 = <div className={t13}>{t21}</div>;
    $[110] = t13;
    $[111] = t21;
    $[112] = t22;
  } else {
    t22 = $[112];
  }
  let t23;
  if ($[113] !== t14 || $[114] !== t15 || $[115] !== t16 || $[116] !== t17 || $[117] !== t18 || $[118] !== t19 || $[119] !== t20 || $[120] !== t22) {
    t23 = <section id={t14} className={t15} style={t16}>{t17}{t18}{t19}{t20}{t22}</section>;
    $[113] = t14;
    $[114] = t15;
    $[115] = t16;
    $[116] = t17;
    $[117] = t18;
    $[118] = t19;
    $[119] = t20;
    $[120] = t22;
    $[121] = t23;
  } else {
    t23 = $[121];
  }
  return t23;
}